import {
    ExpandLess,
    ExpandMore,
    UnfoldLess,
    UnfoldMore
} from '@mui/icons-material';
import {
    Box,
    Button,
    Divider,
    IconButton,
    TableCell,
    TableRow,
    Typography
} from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { ManagedTable } from 'application/Shared/Components/Table';
import { HMS } from 'application/Shared/Components/Usage';
import { FontSizes } from 'application/Shared/Constants';
import { Helpers } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  Objects  */
import { Application } from 'objects/Application';
import { User, UserFullName, UserIconAndName } from 'objects/User';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';

/*  Styles  */
const sxs = {
    text: {
        small: { fontSize: FontSizes[0] }
    }
};

/*  Constants  */
const { isset } = Helpers;

class UsageDailyBreakdown extends Component {
    constructor(props) {
        super(props);
        this.state = {
            csvData: [],
            csvHeaders: [],
            data: [],
            headers: [],
            loaded: false,
            showCollapse: false,
            showExpand: true
        };
    }

    componentDidMount() {
        this.load();
    }

    load = async () => {
        const { reportData } = this.props;

        this.loadHeaders();

        if (Helpers.isEmpty(reportData)) {
            debug.w('No Report Data Found', reportData);
            return;
        }

        const { data, uniqueDates } = reportData;
        if (!Array.isArray(data)) {
            return;
        }

        this.loadData({ data, uniqueDates });
    };

    loadHeaders = async () => {
        const { t, reportSetup, renderingScheduledReport, reportData } =
            this.props;
        const { period } = reportSetup;
        const { uniqueDates } = reportData;

        const sx = sxs.text.small;

        const headers = [];
        const csvHeaders = [
            { key: 'name', label: 'App Name / User Name' },
            { key: 'email', label: 'User Email' },
            { key: 'appName', label: 'App Name' },
            { key: 'usage', label: 'Usage' },
            { key: 'date', label: 'Date' }
        ];

        if (period === 'weekly') {
            if (!renderingScheduledReport) {
                headers.push({
                    align: 'left',
                    field: 'chevron',
                    renderHeader: <strong>&nbsp;</strong>,
                    width: '1%'
                });
            }
            headers.push(
                {
                    align: 'left',
                    field: 'application',
                    renderHeader: <strong>{t('Common.application')}</strong>,
                    width: '30%'
                },
                {
                    align: 'right',
                    field: 'sun',
                    renderHeader: <strong>{t('Common.days_sun')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'mon',
                    renderHeader: <strong>{t('Common.days_mon')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'tues',
                    renderHeader: <strong>{t('Common.days_tue')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'wednes',
                    renderHeader: <strong>{t('Common.days_wed')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'thurs',
                    renderHeader: <strong>{t('Common.days_thu')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'fri',
                    renderHeader: <strong>{t('Common.days_fri')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'saturday',
                    renderHeader: <strong>{t('Common.days_sat')}</strong>,
                    width: '10%'
                },
                {
                    align: 'right',
                    field: 'total',
                    renderHeader: <strong>{t('Common.total')}</strong>,
                    width: '10%'
                }
            );
            this.setState({ csvHeaders, headers });
            return;
        }

        headers.push(
            {
                align: 'left',
                field: 'chevron',
                renderHeader: <strong style={sx}>&nbsp;</strong>,
                squish: true,
                width: '40px'
            },
            {
                align: 'left',
                field: 'application',
                renderHeader: (
                    <strong style={sx}>{t('Common.application')}</strong>
                ),
                width: '300px'
            }
        );

        Object.values(uniqueDates).forEach((d) => {
            const { date, position } = d;

            headers.push({
                align: 'right',
                field: `iudb_header_${position}`,
                renderHeader: <strong style={sx}>{date}</strong>,
                squish: true,
                width: '80px'
            });
        });

        headers.push({
            align: 'right',
            field: 'total',
            renderHeader: <strong style={sx}>Total</strong>,
            squish: true,
            width: '80px'
        });

        this.setState({ csvHeaders, headers });
    };

    loadData = (p) => {
        const { uniqueDates } = p;
        const today = new Date();

        const csvData = [];
        const data = p.data.map((a) => {
            const { application, userBreakdown } = a;

            const appName = application.name;
            Object.values(uniqueDates).forEach((d) => {
                const { date, position } = d;
                const dateObj = new Date(date);
                if (dateObj > today) {
                    return;
                }
                const usage = Helpers.isset(a, 'days', position)
                    ? a.days[position]
                    : 0;
                csvData.push({
                    ...a,
                    appName,
                    date,
                    email: '',
                    name: appName,
                    usage
                });
            });

            if (!userBreakdown) {
                return { ...a, expanded: false };
            }

            userBreakdown.forEach((u) => {
                const { user } = u;

                let email = '';
                if (user) {
                    email = user.email;
                }

                Object.values(uniqueDates).forEach((d) => {
                    const { date, position } = d;
                    const dateObj = new Date(date);
                    if (dateObj > today) {
                        return;
                    }

                    const usage = Helpers.isset(u, 'days', position)
                        ? u.days[position]
                        : 0;
                    csvData.push({
                        ...user,
                        appName,
                        date,
                        email,
                        usage
                    });
                });
            });

            return { ...a, expanded: false };
        });

        this.setState({ csvData, data, loaded: true });
    };

    handleAll = (expanded) => {
        let { data } = this.state;
        data = data.map((d) => ({ ...d, expanded }));
        this.setState({ data, showCollapse: expanded, showExpand: !expanded });
    };

    handleExpand = (row) => {
        const { total } = this.state;
        let { data } = this.state;

        let expandedCount = 0;
        let collapsedCount = 0;

        data = data.map((d) => {
            const { application } = d;
            let { expanded } = d;
            if (application.id !== row.application.id) {
                if (expanded) {
                    expandedCount += 1;
                } else {
                    collapsedCount += 1;
                }
                return d;
            }

            expanded = !expanded;
            if (expanded) {
                expandedCount += 1;
            } else {
                collapsedCount += 1;
            }

            return { ...d, expanded };
        });

        let showExpand = false;
        let showCollapse = false;
        if (expandedCount > 0) {
            if (expandedCount !== total) {
                showCollapse = true;
            }
        } else {
            showExpand = true;
        }

        if (collapsedCount > 0) {
            if (collapsedCount !== total) {
                showExpand = true;
            }
        } else {
            showCollapse = true;
        }

        this.setState({ data, showCollapse, showExpand });
    };

    renderButtons = () => {
        const { t } = this.props;
        const { showExpand, showCollapse } = this.state;

        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '8px',
                    marginTop: '8px',
                    width: '100%'
                }}
            >
                {showCollapse && (
                    <Button
                        variant="outlined"
                        className="no-print"
                        color="primary"
                        onClick={() => this.handleAll(false)}
                    >
                        <UnfoldLess fontSize="small" />
                        {t('Reports.collapse_all')}
                    </Button>
                )}
                {showExpand && (
                    <Button
                        variant="contained"
                        className="no-print"
                        color="primary"
                        onClick={() => this.handleAll(true)}
                        style={{ marginLeft: '8px' }}
                    >
                        <UnfoldMore fontSize="small" />
                        {t('Reports.expand_all')}
                    </Button>
                )}
            </Box>
        );
    };

    renderRow = (row) => {
        const { t, renderingScheduledReport } = this.props;
        const { data } = this.state;

        return (
            <Application
                data={row.application}
                key={`usage_app__${row.application.id}`}
            >
                <TableRow sx={{ backgroundColor: 'table.rowBackground' }}>
                    {!renderingScheduledReport && (
                        <TableCell>
                            <IconButton
                                aria-label="expand row"
                                size="small"
                                onClick={() => this.handleExpand(row)}
                                sx={{ color: 'icon.textColor' }}
                            >
                                {row.expanded ? (
                                    <ExpandLess fontSize="small" />
                                ) : (
                                    <ExpandMore fontSize="small" />
                                )}
                            </IconButton>
                        </TableCell>
                    )}
                    <TableCell>
                        <ApplicationNameLinkCell row={row} nameNoTruncate />
                    </TableCell>
                    {row.days.map((usage) => (
                        <TableCell key={`usage_${Math.random()}`} align="right">
                            <HMS seconds={usage} sx={sxs.text.small} />
                            {(row.expanded || renderingScheduledReport) && (
                                <>
                                    <br />
                                    <small>
                                        <HMS
                                            seconds={
                                                row?.userBreakdown?.length > 0
                                                    ? Math.round(
                                                          usage /
                                                              row.userBreakdown
                                                                  .length
                                                      )
                                                    : 0
                                            }
                                            sx={sxs.text.small}
                                        />
                                    </small>
                                </>
                            )}
                        </TableCell>
                    ))}
                    <TableCell align="right">
                        <HMS seconds={row.usage} sx={sxs.text.small} />
                        {(row.expanded || renderingScheduledReport) && (
                            <>
                                <br />
                                <small>
                                    <HMS
                                        seconds={Math.round(
                                            row.usage / row.userBreakdown.length
                                        )}
                                        sx={sxs.text.small}
                                    />
                                </small>
                            </>
                        )}
                    </TableCell>
                </TableRow>
                {(row.expanded || data.length === 1) && (
                    <TableRow
                        sx={{
                            backgroundColor: 'table.subtableBackground'
                        }}
                    >
                        <TableCell
                            colSpan={100}
                            sx={{
                                backgroundColor: 'table.subtableHeader'
                            }}
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                width="100%"
                            >
                                <ApplicationNameLinkCell />
                                <Box ml="4px">{t('Common.breakdown')}</Box>
                            </Box>
                            <Box mt="8px">
                                <strong
                                    style={{
                                        width: '140px'
                                    }}
                                >
                                    {t('Reports.UsageReport.application_users')}
                                    :{' '}
                                </strong>{' '}
                                {row.userBreakdown.length}
                                <br />
                                <strong style={{ width: '140px' }}>
                                    {t('Reports.UsageReport.average_usage')}:{' '}
                                </strong>{' '}
                                <HMS
                                    seconds={
                                        row?.userBreakdown?.length > 0
                                            ? Math.round(
                                                  row.usage /
                                                      row.userBreakdown.length
                                              )
                                            : 0
                                    }
                                />
                            </Box>
                        </TableCell>
                    </TableRow>
                )}
                {(row.expanded || data.length === 1) &&
                    row.userBreakdown
                        .sort((a, b) => b.usage - a.usage)
                        .map((user, index) => (
                            <TableRow
                                key={`ubreakdown_${row.applicationId}_u${user.user.id}`}
                                sx={{
                                    backgroundColor: 'table.subtableBackground'
                                }}
                            >
                                {!renderingScheduledReport && (
                                    <TableCell>
                                        <small>{index + 1}.</small>
                                    </TableCell>
                                )}
                                <TableCell>
                                    <User data={user.user}>
                                        {!renderingScheduledReport && (
                                            <UserIconAndName />
                                        )}
                                        {renderingScheduledReport && (
                                            <UserFullName />
                                        )}
                                    </User>
                                </TableCell>
                                {user.days.map((userDailyUsage) => (
                                    <TableCell
                                        key={`ubreakdown_${
                                            row.applicationId
                                        }_u${user.user.id}_${Math.random()}`}
                                        align="right"
                                    >
                                        <HMS
                                            seconds={userDailyUsage}
                                            sx={sxs.text.small}
                                        />
                                    </TableCell>
                                ))}
                                <TableCell align="right">
                                    <HMS
                                        seconds={user.usage}
                                        sx={sxs.text.small}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
            </Application>
        );
    };

    render() {
        const { t, reportWrapper } = this.props;
        const { loaded, headers, csvHeaders, csvData, data } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <Box
                className="notResponsive"
                sx={{
                    margin: !isset(this.props, 'reportWrapperProps', 'height')
                        ? '24px'
                        : '0px'
                }}
            >
                {!reportWrapper && (
                    <>
                        <Typography variant="h5">
                            {t('Reports.UsageReport.usage_daily_breakdown')}
                        </Typography>
                        <Divider
                            sx={{
                                borderBottomWidth: '4px',
                                borderColor: 'border.darkerDivider',
                                marginBottom: '16px',
                                marginTop: '8px'
                            }}
                        />
                    </>
                )}
                {this.renderButtons()}
                <ManagedTable
                    csvData={csvData}
                    csvExport
                    csvExportFields={csvHeaders}
                    csvExportFileName="usage_breakdown_daily"
                    headers={headers}
                    manageRows
                    renderBody={this.renderRow}
                    renderBodyKey={null}
                    renderFooter
                    rows={data}
                    rowsPerPage={-1}
                    searchEnabled={false}
                    sortDirection="DESC"
                    sortFieldType="number"
                    sortKey="usage"
                />
            </Box>
        );
    }
}

export default withTranslation()(UsageDailyBreakdown);
